import { Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom, Observable, of, tap } from 'rxjs';
import { PaginationLimit } from 'src/app/core/constants/dropdown-contants';
import { BoardNameService } from 'src/app/core/services/board-name/board-name.service';

@Injectable({
  providedIn: 'root'
})
export class CacheBoardNameService {
  private boardListSubject = new BehaviorSubject<any>([]);
  public boardList$ = this.boardListSubject.asObservable();
  totalBoardCount: number = 0;

  constructor(private _boardService: BoardNameService) { }

  previousPagination = {
    skip: 0,
    limit: PaginationLimit.board
  };
  previousFilter: string = '';

  async fetchBoards(pagination: any, filter: string = ''): Promise<{ data: any[], total: number }> {
    // Check if pagination or filter has changed
    if (this.previousPagination?.skip !== pagination.skip || 
        this.previousPagination?.limit !== pagination.limit || 
        this.previousFilter !== filter) {
      // Reset cache if parameters changed
      this.previousPagination = { ...pagination };
      this.previousFilter = filter;
      this.boardListSubject.next([]);
    } else {
      // Return cached data if available and parameters haven't changed
      if (this.boardListSubject.value.length > 0) {
        return { 
          data: this.boardListSubject.value, 
          total: this.totalBoardCount 
        };
      }
    }

    try {
      // Fetch data from the service
      const res = await lastValueFrom(
        this._boardService.getBoardListName(pagination, filter)
      );

      // Process successful response
      if (res?.statusCode === 200 && res?.data?.data?.length > 0) {
        const boardList = res.data.data;
        const total = res.data.total;

        // Update cache
        this.boardListSubject.next(boardList);
        this.totalBoardCount = total;

        return { data: boardList, total };
      } else {
        return { data: [], total: 0 };
      }
    } catch (error) {
      console.error('Error fetching boards:', error);
      return { data: [], total: 0 };
    }
  }

  getBoardById(boardId: string): Observable<any> {
    // Check cache first
    const existingBoard = this.boardListSubject.value.find(b => b.id === boardId);
    if (existingBoard) {
      return of(existingBoard);
    }

    // Fetch from API if not in cache
    return this._boardService.getBoardById(boardId).pipe(
      tap((res: any) => {
        if (res?.data) {
          const board = {
            id: res.data.id,
            name: res.data.boardName
          };
          // Update cache with new board
          const updatedList = [...this.boardListSubject.value, board];
          this.boardListSubject.next(updatedList);
        }
      })
    );
  }

  clearCache(): void {
    this.boardListSubject.next([]);
    this.totalBoardCount = 0;
    this.previousPagination = {
      skip: 0,
      limit: PaginationLimit.board
    };
    this.previousFilter = '';
  }
}

interface BoardService {
  getBoardListName(pagination: any, filter: string): Observable<any>;
  getBoardById(id: string): Observable<any>;
}
